import {
  ArrowRight,
  ChartNoAxesCombined,
  CircleDollarSign,
  Facebook,
  Instagram,
  Landmark,
  Linkedin,
  Mail,
  Receipt,
  ShieldCheck,
  Sparkles,
  Wallet,
} from 'lucide-react';

function App() {
  const navLinks = ['Features', 'How It Works', 'Contact'];
  const featureCards = [
    {
      icon: Receipt,
      title: 'AI Receipt Scanning',
      description: 'Snap a photo and let AI extract and categorize instantly.',
    },
    {
      icon: ShieldCheck,
      title: 'Secure Bank Sync',
      description: 'Connect securely with Plaid.',
    },
    {
      icon: ChartNoAxesCombined,
      title: 'Smart Insights',
      description: 'Visualize spending trends.',
    },
    {
      icon: Sparkles,
      title: 'Auto Categorization',
      description: 'Automatic sorting of expenses.',
    },
  ];

  const howItWorks = [
    {
      icon: Landmark,
      title: 'Connect your bank',
      description: "Securely link your bank account to automatically pull your transactions.",
    },
    {
      icon: Receipt,
      title: 'Add receipts (optional)',
      description: 'Upload receipts when you want precise detail on where and how much you spent.',
    },
    {
      icon: CircleDollarSign,
      title: 'Track exact spending',
      description: 'Combine transaction feeds and receipt details for a clearer view of every expense.',
    },
  ];

  const socialLinks = [
    { icon: Linkedin, label: 'LinkedIn' },
    { icon: Instagram, label: 'Instagram' },
    { icon: Facebook, label: 'Facebook' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_20%_20%,rgba(79,70,229,0.35),transparent_40%),radial-gradient(circle_at_80%_0%,rgba(16,185,129,0.25),transparent_35%),linear-gradient(125deg,#020617,#0b1432,#042329)] bg-[length:180%_180%] animate-gradient-shift" />

      <header className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-6 lg:px-10">
        <a href="#top" className="text-xl font-semibold tracking-tight sm:text-2xl">
          Spendivo
        </a>
        <nav className="hidden items-center gap-7 text-sm text-slate-300 md:flex">
          {navLinks.map((link) => (
            <a key={link} href={`#${link.toLowerCase().replace(/\s+/g, '-')}`} className="transition hover:text-white">
              {link}
            </a>
          ))}
        </nav>
        <button className="rounded-full border border-emeraldBrand/60 bg-emeraldBrand px-5 py-2.5 text-sm font-medium text-slate-950 transition hover:-translate-y-0.5 hover:bg-emerald-300">
          Get Early Access
        </button>
      </header>

      <main className="mx-auto w-full max-w-7xl px-6 pb-20 lg:px-10">
        <section id="top" className="grid items-center gap-10 pb-16 pt-8 md:grid-cols-2 md:pt-16">
          <div className="animate-fade">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-indigoBrand/40 bg-indigoBrand/15 px-3 py-1 text-xs text-indigo-200">
              <Wallet size={14} /> Fintech automation made practical
            </p>
            <h1 className="max-w-xl text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              Smarter Spending Starts Here.
            </h1>
            <p className="mt-6 max-w-xl text-base text-slate-300 sm:text-lg">
              Track expenses automatically, scan receipts with AI, and connect your bank securely
              - all in one powerful app.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button className="rounded-full bg-indigoBrand px-6 py-3 text-sm font-semibold transition hover:-translate-y-0.5 hover:bg-indigo-500">
                Get Early Access
              </button>
              <button className="inline-flex items-center gap-2 rounded-full border border-slate-400/40 px-6 py-3 text-sm font-semibold text-slate-100 transition hover:border-emeraldBrand/70 hover:text-emerald-200">
                See How It Works <ArrowRight size={16} />
              </button>
            </div>
          </div>
          <div className="animate-float rounded-3xl border border-slate-700/70 bg-slate-900/70 p-6 shadow-2xl shadow-indigoBrand/20 backdrop-blur">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Spendivo Dashboard</h3>
              <span className="rounded-full bg-emeraldBrand/20 px-3 py-1 text-xs text-emerald-200">
                +12% Savings
              </span>
            </div>
            <div className="space-y-4">
              <div className="rounded-2xl bg-slate-800/70 p-4">
                <p className="text-xs text-slate-400">Monthly spend</p>
                <p className="mt-1 text-2xl font-semibold">$4,280</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-indigoBrand/20 p-3">
                  <p className="text-xs text-indigo-200">Food</p>
                  <p className="text-sm font-semibold">$680</p>
                </div>
                <div className="rounded-xl bg-emeraldBrand/20 p-3">
                  <p className="text-xs text-emerald-200">Bills</p>
                  <p className="text-sm font-semibold">$1,140</p>
                </div>
                <div className="rounded-xl bg-sky-500/20 p-3">
                  <p className="text-xs text-sky-200">Travel</p>
                  <p className="text-sm font-semibold">$320</p>
                </div>
              </div>
              <div className="rounded-2xl bg-slate-800/70 p-4">
                <p className="text-xs text-slate-400">AI suggestion</p>
                <p className="text-sm text-slate-200">
                  You can save $180/month by optimizing recurring subscriptions.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="py-16">
          <h2 className="mb-10 text-3xl font-semibold sm:text-4xl">Powerful Features</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {featureCards.map(({ icon: Icon, title, description }) => (
              <article
                key={title}
                className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 transition duration-300 hover:-translate-y-1 hover:border-emeraldBrand/40 hover:shadow-xl hover:shadow-emeraldBrand/10"
              >
                <div className="mb-4 inline-flex rounded-xl bg-indigoBrand/20 p-3 text-indigo-200">
                  <Icon size={20} />
                </div>
                <h3 className="mb-2 text-lg font-semibold">{title}</h3>
                <p className="text-sm text-slate-300">{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="how-it-works" className="py-16">
          <h2 className="mb-10 text-3xl font-semibold sm:text-4xl">How It Works</h2>
          <p className="mb-8 max-w-3xl text-slate-300">
            Spendivo first syncs your bank transactions automatically. Then you can optionally add
            receipts to track exactly where each dollar was spent.
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            {howItWorks.map(({ icon: Icon, title, description }, index) => (
              <article key={title} className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6">
                <div className="mb-4 flex items-center justify-between">
                  <div className="rounded-xl bg-emeraldBrand/20 p-3 text-emerald-200">
                    <Icon size={20} />
                  </div>
                  <span className="text-xs text-slate-400">Step {index + 1}</span>
                </div>
                <h3 className="text-lg font-semibold">{title}</h3>
                <p className="mt-2 text-sm text-slate-300">{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section
          id="contact"
          className="rounded-3xl border border-indigoBrand/30 bg-gradient-to-r from-indigoBrand/20 via-slate-900/80 to-emeraldBrand/20 px-6 py-12 text-center sm:px-10"
        >
          <h2 className="text-3xl font-semibold sm:text-4xl">Take Control of Your Finances Today</h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">
            Join the waitlist and be first to experience smarter expense tracking with Spendivo.
          </p>
          <form className="mx-auto mt-8 flex max-w-xl flex-col gap-3 sm:flex-row">
            <label htmlFor="email" className="sr-only">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              placeholder="Enter your email"
              className="w-full rounded-full border border-slate-500/60 bg-slate-950/70 px-5 py-3 text-slate-100 placeholder:text-slate-400 focus:border-indigo-300 focus:outline-none"
            />
            <button
              type="submit"
              className="rounded-full bg-emeraldBrand px-6 py-3 font-semibold text-slate-950 transition hover:bg-emerald-300"
            >
              Get Early Access
            </button>
          </form>
        </section>
      </main>

      <footer className="border-t border-slate-800/80 py-10">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-6 lg:flex-row lg:items-start lg:justify-between lg:px-10">
          <div>
            <h3 className="text-xl font-semibold">Spendivo</h3>
            <p className="mt-2 max-w-xs text-sm text-slate-400">
              Smarter tools to track, understand, and improve every dollar you spend.
            </p>
          </div>
          <div className="flex flex-col gap-2 text-sm text-slate-300">
            {navLinks.map((link) => (
              <a key={link} href={`#${link.toLowerCase().replace(/\s+/g, '-')}`} className="transition hover:text-white">
                {link}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {socialLinks.map(({ icon: Icon, label }) => (
              <a
                key={label}
                href="#top"
                aria-label={label}
                className="rounded-full border border-slate-700 p-2 text-slate-300 transition hover:border-emeraldBrand/60 hover:text-emerald-200"
              >
                <Icon size={16} />
              </a>
            ))}
            <a
              href="mailto:hello@spendivo.app"
              className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-3 py-2 text-sm text-slate-300 transition hover:border-indigo-300 hover:text-indigo-200"
            >
              <Mail size={14} />
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
